import React, { useMemo } from 'react';
import { AbsoluteFill, Easing, Sequence, useVideoConfig } from 'remotion';
import { TransitionSeries, linearTiming } from '@remotion/transitions';
import { HeroPropsSchema, secondsToFrames, type HeroAspect, type HeroProps } from './schema';
import { buildHeroTimeline } from './timeline';
import { loadHeroFonts } from './fonts';
import { ClipLayer } from './components/ClipLayer';
import { threadWipe } from './components/ThreadWipe';
import { KineticPhrase } from './components/KineticPhrase';
import { HeroCaptions } from './components/HeroCaptions';
import { ProductInsert } from './components/ProductInsert';
import { LogoClose } from './components/LogoClose';
import { AudioBed } from './components/AudioBed';

loadHeroFonts();

/**
 * Shared hero assembly. HeroSquare / HeroVertical are separate compositions over this component; the vertical
 * master is not a crop — it takes its own clip list and its own safe zones (props.aspect).
 */
export const Hero: React.FC<HeroProps> = (rawProps) => {
  const props = useMemo(() => HeroPropsSchema.parse(rawProps), [rawProps]);
  const { fps } = useVideoConfig();
  const timeline = useMemo(() => buildHeroTimeline(props, fps), [props, fps]);

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      <TransitionSeries name="hero-content">
        {timeline.segments.map((segment, i) => {
          const key = `${segment.kind}-${segment.clipIndex}-${i}`;
          return [
            segment.transitionIn === 'thread-wipe' ? (
              <TransitionSeries.Transition
                key={`${key}-wipe`}
                presentation={threadWipe()}
                timing={linearTiming({ durationInFrames: timeline.wipeFrames, easing: Easing.inOut(Easing.cubic) })}
              />
            ) : null,
            <TransitionSeries.Sequence key={key} durationInFrames={segment.durationInFrames} name={key}>
              <ClipLayer segment={segment} clipVolume={props.clipVolume} />
            </TransitionSeries.Sequence>,
          ];
        })}
      </TransitionSeries>

      {props.productInserts.map((insert, i) => (
        <Sequence
          key={`insert-${i}`}
          from={secondsToFrames(insert.atSec, fps)}
          durationInFrames={secondsToFrames(insert.durationSec, fps)}
          name={`insert-${i}`}
        >
          <ProductInsert imageUrl={insert.imageUrl} durationInFrames={secondsToFrames(insert.durationSec, fps)} aspect={props.aspect} />
        </Sequence>
      ))}

      {props.phrases.map((phrase, i) => (
        <Sequence
          key={`phrase-${i}`}
          from={secondsToFrames(phrase.atSec, fps)}
          durationInFrames={secondsToFrames(phrase.durationSec, fps)}
          name={`phrase-${i}`}
        >
          <KineticPhrase
            text={phrase.text}
            accentWord={phrase.accentWord}
            durationInFrames={secondsToFrames(phrase.durationSec, fps)}
            aspect={props.aspect}
          />
        </Sequence>
      ))}

      {props.captions && props.captions.words.length > 0 ? (
        <Sequence durationInFrames={timeline.contentFrames} name="captions">
          <HeroCaptions words={props.captions.words} aspect={props.aspect} />
        </Sequence>
      ) : null}

      {timeline.logo ? (
        <Sequence from={timeline.logo.from} durationInFrames={timeline.logo.durationInFrames} name="logo-close">
          <LogoClose url={timeline.logo.url} fit={props.logoCloseFit} background={props.logoCloseBackground} />
        </Sequence>
      ) : null}

      <AudioBed props={props} timeline={timeline} />
    </AbsoluteFill>
  );
};

const forceAspect = (aspect: HeroAspect): React.FC<HeroProps> => {
  const Forced: React.FC<HeroProps> = (props) => <Hero {...props} aspect={aspect} />;
  Forced.displayName = aspect === '1:1' ? 'HeroSquare' : 'HeroVertical';
  return Forced;
};

/** 1080x1080 master. */
export const HeroSquare = forceAspect('1:1');
/** 1080x1920 master (top/bottom 15% kept clear for platform chrome). */
export const HeroVertical = forceAspect('9:16');

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
